import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, History, LayoutDashboard, Shield, LogOut,
  Settings, PanelRightClose, PanelLeftClose, CreditCard,
  Link2, HelpCircle, Zap, TrendingUp,
  Sparkles, ArrowUpRight, Circle, Users
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';

function useCredits(user) {
  const [credits, setCredits] = useState({ used: 0, limit: 0, loading: true });
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchCredits = async () => {
      try {
        const res = await api.get('/api/sidebar/credits');
        if (!cancelled) setCredits({ used: res.data.searches_used || 0, limit: res.data.searches_limit || 0, loading: false });
      } catch { if (!cancelled) setCredits(c => ({ ...c, loading: false })); }
    };
    fetchCredits();
    const interval = setInterval(fetchCredits, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);
  return credits;
}

function usePipelinePulse(user) {
  const [pulse, setPulse] = useState({ today: 0, bestScore: 0, bestCompany: '', loading: true });
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchPulse = async () => {
      try {
        const res = await api.get('/api/sidebar/pipeline-pulse');
        if (!cancelled) setPulse({ today: res.data.today_leads || 0, bestScore: res.data.best_score || 0, bestCompany: res.data.best_company || '', loading: false });
      } catch { if (!cancelled) setPulse(p => ({ ...p, loading: false })); }
    };
    fetchPulse();
    const interval = setInterval(fetchPulse, 5000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user]);
  return pulse;
}

function MiniSparkline({ data, width, height, color }) {
  const w = width || 60;
  const h = height || 18;
  const c = color || 'var(--accent-primary)';
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - ((val - min) / range) * (h - 4) - 2;
    return x + ',' + y;
  }).join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block', flexShrink: 0 }}>
      <polyline points={points} fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Sidebar({ isOpen, isCollapsed, onClose, onToggleCollapse, activeTab, onTabChange, isAdmin, onLogout, isSearching, activeJobs }) {
  const { user } = useAuth();
  const { resolvedTheme, setTheme } = useTheme();
  const navigate = useNavigate();
  const credits = useCredits(user);
  const pulse = usePipelinePulse(user);

  // Refetch credits when search state changes
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const res = await api.get('/api/sidebar/credits');
        // Force credits update by dispatching a custom event
        window.dispatchEvent(new CustomEvent('credits-updated', { detail: res.data }));
      } catch {}
    };
    if (isSearching !== undefined) fetchCredits();
  }, [isSearching]);
  const [sparklineData] = useState([2, 3, 5, 4, 7, 8, 6, 9, 11, 8, 12, 10]);
  const [planData, setPlanData] = useState(null);
  
  useEffect(() => {
    api.get('/api/billing/plans/full').then(r => {
      setPlanData(r.data?.current_plan_data || {});
    }).catch(() => {});
  }, [user]);

  const currentPlan = user?.plan || 'free';
  const planLabel = planData?.name || currentPlan?.charAt(0).toUpperCase() + currentPlan?.slice(1);
  const activeJobsCount = activeJobs?.length || 0;
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const primaryItems = [
    { id: 'search', icon: Search, label: 'New Search', elevated: true },
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'history', icon: History, label: 'Lead History' },
  ];
  const isSdrOrTeamLead = user?.team_role === 'sdr' || user?.team_role === 'team_lead';
  const secondaryItems = [
    ...(!isSdrOrTeamLead ? [{ id: 'integrations', icon: Link2, label: 'Integrations' }] : []),
    ...(user?.organization_id && user?.team_status === 'active' ? [{ id: 'team', icon: Users, label: 'Team' }] : []),
    ...(!isSdrOrTeamLead ? [{ id: 'billing', icon: CreditCard, label: 'Billing & Usage' }] : []),
    { id: 'help', icon: HelpCircle, label: 'Help & Support' },
    ...(!isSdrOrTeamLead ? [{ id: 'settings', icon: Settings, label: 'Settings' }] : []),
  ];
  const { pending2FA } = useAuth();
  const showAdmin = isAdmin && !pending2FA;
  const adminItems = showAdmin ? [{ id: 'admin', icon: Shield, label: 'Admin Panel' }] : [];

  const handleNavClick = (id) => { onTabChange(id); if (isMobile) onClose(); };
  const sidebarWidth = isCollapsed ? 68 : 260;

  const renderNavButton = (item, isAdminItem) => {
    const Icon = item.icon;
    const active = isAdminItem ? (activeTab?.startsWith('admin')) : (activeTab === item.id);
    const styles = {
      button: {
        display: 'flex', alignItems: 'center', gap: isCollapsed ? 0 : 10,
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        width: '100%', padding: isCollapsed ? 10 : '10px 14px',
        marginBottom: 2, borderRadius: 10, border: 'none', cursor: 'pointer',
        background: active
          ? (item.elevated ? 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.04))' : 'var(--accent-glow)')
          : 'transparent',
        color: active
          ? (item.elevated ? 'var(--accent-amber)' : 'var(--text-primary)')
          : 'var(--text-secondary)',
        fontSize: 'var(--text-sm)', fontWeight: active ? 600 : 400,
        transition: 'all 0.2s cubic-bezier(0.22, 1, 0.36, 1)',
        whiteSpace: 'nowrap', textAlign: 'left', position: 'relative',
        overflow: 'hidden', fontFamily: 'inherit', outline: 'none',
      },
      indicator: {
        position: 'absolute', left: 0, top: '50%',
        transform: 'translateY(-50%) scaleY(' + (active ? 1 : 0) + ')',
        width: 3, height: 20, borderRadius: '0 3px 3px 0',
        background: item.elevated
          ? 'linear-gradient(180deg, #F59E0B, #D97706)'
          : 'var(--accent-primary)',
        transition: 'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)',
        opacity: active ? 1 : 0,
      },
    };

    return (
      <button
        key={item.id}
        onClick={() => handleNavClick(item.id)}
        title={isCollapsed ? item.label : undefined}
        style={styles.button}
        className={!active ? 'sidebar-nav-item' : ''}
      >
        <div style={styles.indicator} />
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, flexShrink: 0, position: 'relative' }}>
          <Icon size={18} strokeWidth={active ? 2.2 : 1.8} />
          {item.elevated && (
            <span style={{ position: 'absolute', inset: -6, borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,158,11,0.15) 0%, transparent 70%)', animation: 'sidebarPulseRing 3s ease-in-out infinite' }} />
          )}
        </span>
        {!isCollapsed && <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>}
        {item.id === 'search' && activeJobsCount > 0 && !isCollapsed && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: '2px 8px', borderRadius: 10, background: 'rgba(16,185,129,0.12)', color: 'var(--success)', fontSize: 10, fontWeight: 600, animation: 'sidebarBadgePulse 2s ease-in-out infinite' }}>
            <Circle size={6} fill="var(--success)" stroke="none" />{activeJobsCount} active
          </span>
        )}
      </button>
    );
  };

  const userInitials = (user?.full_name || user?.email || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  const creditPercent = credits.loading ? 30 : (credits.limit > 0 ? (credits.used / credits.limit) * 100 : 0);
  const creditLow = credits.limit > 0 && (credits.used / credits.limit) > 0.8;

  return (
    <aside style={{
      width: sidebarWidth, height: '100dvh', background: 'var(--sidebar-bg)', boxSizing: 'border-box',
      borderRight: '1px solid var(--border-primary)', display: 'flex', flexDirection: 'column',
      position: isMobile ? 'fixed' : 'relative',
      left: 0, top: 0,
      transform: isMobile ? (isOpen ? 'translateX(0)' : 'translateX(-100%)') : 'none',
      zIndex: 700,
      transition: 'transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), width 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
      boxShadow: isCollapsed ? 'none' : '0 0 40px rgba(0,0,0,0.06)', overflow: 'hidden',
    }}>

      {/* ZONE 1: BRAND + PULSE */}
      <div style={{ padding: isCollapsed ? 14 : '18px 20px', borderBottom: '1px solid var(--border-primary)', display: 'flex', alignItems: 'center', gap: 10, minHeight: 56, flexShrink: 0 }}>
        
        {!isCollapsed && (
<a href="/dashboard" style={{ flex: 1, minWidth: 0, textDecoration: "none", cursor: "pointer", display: "flex", alignItems: "center" }}>            <img src="/iLeadX-logo.png" alt="iLeadX" style={{ height: 32, width: "auto" }} />
            
</a>        )}
        <button onClick={onToggleCollapse} className="desktop-only" style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-tertiary)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}>
          {isCollapsed ? <PanelRightClose size={14} /> : <PanelLeftClose size={14} />}
        </button>
      </div>

      {/* SYSTEM PULSE BAR */}
      <div style={{ height: 2, width: '100%', background: 'var(--bg-tertiary)', position: 'relative', overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ height: '100%', width: '40%', background: 'linear-gradient(90deg, transparent, var(--accent-primary), transparent)', animation: 'systemPulse 4s ease-in-out infinite', borderRadius: 1 }} />
      </div>

      {/* ZONE 2: NAVIGATION */}
      
      <style>{`
        nav::-webkit-scrollbar {
          width: 4px;
        }
        nav::-webkit-scrollbar-track {
          background: var(--scrollbar-track);
        }
        nav::-webkit-scrollbar-thumb {
          background: var(--scrollbar-thumb);
          border-radius: 4px;
        }
      `}</style>


      <nav style={{ flex: 1, padding: isCollapsed ? '8px 6px' : '12px 10px', overflowY: 'auto', overflowX: 'hidden', width: '100%', boxSizing: 'border-box', scrollbarGutter: 'stable', scrollbarWidth: 'thin' }}>
        {primaryItems.map(item => renderNavButton(item, false))}
        {!isCollapsed && <div style={{ height: 1, background: 'var(--border-primary)', margin: '8px 6px', opacity: 0.5 }} />}
        {secondaryItems.map(item => renderNavButton(item, false))}
        {adminItems.length > 0 && (
          <>
            {!isCollapsed && <div style={{ height: 1, background: 'var(--border-primary)', margin: '8px 6px', opacity: 0.5 }} />}
            {adminItems.map(item => renderNavButton(item, true))}
          </>
        )}
      </nav>

      {/* ZONE 3: INTELLIGENCE WIDGETS */}
      {!isCollapsed && (
        <div style={{ padding: '8px 10px 10px', borderTop: '1px solid var(--border-primary)', flexShrink: 0 }}>
          {/* Pipeline Pulse */}
          <button onClick={() => { pulse.bestCompany ? navigate('/dashboard/company/' + encodeURIComponent(pulse.bestCompany)) : onTabChange('search'); }}
            style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', borderRadius: 12, border: '1px solid var(--border-primary)', background: 'var(--bg-tertiary)', cursor: 'pointer', marginBottom: 8, fontFamily: 'inherit', textAlign: 'left', transition: 'all 0.2s ease' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary)'; e.currentTarget.style.boxShadow = 'var(--shadow-glow)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-primary)'; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                <TrendingUp size={10} />Pipeline Pulse
              </div>
              <div style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>{pulse.loading ? '...' : pulse.today}</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 10, marginTop: 2 }}>leads found today</div>
              {pulse.bestCompany && (
                <div style={{ color: 'var(--accent-amber)', fontSize: 10, fontWeight: 500, marginTop: 4, display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Sparkles size={9} />Top: {(pulse.bestCompany || '').length > 28 ? (pulse.bestCompany || '').slice(0, 28) + '...' : pulse.bestCompany} ({pulse.bestScore})
                </div>
              )}
            </div>
            <MiniSparkline data={sparklineData} />
          </button>

          {/* Search Credits */}
          <div style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid var(--border-primary)', background: 'var(--bg-tertiary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Searches</span>
              {!credits.loading && credits.limit > 0 && (
                <span style={{ color: creditLow ? 'var(--accent-amber)' : 'var(--text-secondary)', fontSize: 11, fontWeight: 600 }}>{credits.used}/{credits.limit}</span>
              )}
            </div>
            <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-primary)', overflow: 'hidden', marginBottom: 6 }}>
              <div style={{ height: '100%', borderRadius: 2, background: credits.loading ? 'var(--bg-tertiary)' : (creditLow ? 'linear-gradient(90deg, #F59E0B, #EF4444)' : 'linear-gradient(90deg, #2563EB, #3B82F6)'), width: creditPercent + '%', transition: 'width 0.6s cubic-bezier(0.22, 1, 0.36, 1)', animation: credits.loading ? 'sidebarShimmer 2s ease-in-out infinite' : 'none' }} />
            </div>
            <div style={{ color: 'var(--text-tertiary)', fontSize: 10, display: 'flex', justifyContent: 'space-between' }}>
              <span>Resets monthly</span>
              {creditLow && (
                <button onClick={() => onTabChange('billing')} style={{ background: 'none', border: 'none', color: 'var(--accent-amber)', cursor: 'pointer', fontSize: 10, fontWeight: 600, padding: 0, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 2 }}>
                  Upgrade <ArrowUpRight size={9} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ZONE 4: USER */}
      <div style={{ padding: isCollapsed ? '8px 6px' : '10px', borderTop: '1px solid var(--border-primary)', flexShrink: 0 }}>


        <div style={{ display: 'flex', alignItems: 'center', gap: isCollapsed ? 0 : 10, justifyContent: isCollapsed ? 'center' : 'flex-start', marginBottom: isCollapsed ? 4 : 8 }}>
          <button onClick={() => onTabChange('settings')}
            style={{ width: 34, height: 34, borderRadius: 10, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, transition: 'box-shadow 0.2s ease', background: user?.avatar_url ? 'transparent' : 'var(--accent-gradient)', color: user?.avatar_url ? 'transparent' : '#fff', overflow: 'hidden', padding: 0 }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 0 3px var(--accent-glow)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" referrerPolicy="no-referrer" crossOrigin="anonymous" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              userInitials
            )}
          </button>
          {!isCollapsed && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.3 }}>{user?.full_name || user?.email?.split('@')[0] || 'User'}</div>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{planLabel} Plan</div>
            </div>
          )}
        </div>

        {!isCollapsed && planData?.upgrade_message && (
          <button onClick={() => onTabChange('billing')}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, width: '100%', padding: '6px 12px', borderRadius: 8, border: '1px dashed var(--accent-primary)', background: 'transparent', color: 'var(--accent-primary)', cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', marginBottom: 8, transition: 'all 0.15s ease' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--accent-glow)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <Zap size={11} />{planData.upgrade_message}
          </button>
        )}

        <button onClick={onLogout}
          style={{ display: 'flex', alignItems: 'center', gap: isCollapsed ? 0 : 8, justifyContent: isCollapsed ? 'center' : 'flex-start', width: '100%', padding: isCollapsed ? 8 : '8px 12px', borderRadius: 10, border: '1px solid var(--border-primary)', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 'var(--text-sm)', fontFamily: 'inherit', transition: 'all 0.15s ease' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--error)'; e.currentTarget.style.color = 'var(--error)'; e.currentTarget.style.background = 'var(--error-bg)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-primary)'; e.currentTarget.style.color = 'var(--text-tertiary)'; e.currentTarget.style.background = 'transparent'; }}>
          <LogOut size={15} />{!isCollapsed && 'Logout'}
        </button>
      </div>
    </aside>
  );
}
